import { CheckCircle2, Clock } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateSubmissionDialog } from '@/features/memorization/create-submission-dialog';
import { useSubmissions } from '@/features/memorization/hooks';
import { SUBMISSION_STATUS_LABELS } from '@/features/memorization/labels';
import type { SubmissionStatus } from '@/features/memorization/types';
import { useSurahLookup } from '@/features/quran/hooks';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 20;

/** Same icon-plus-highlight language target-list.tsx's StatusIcon introduced (Phase 8, Pilar C). */
function StatusIcon({ status }: { readonly status: SubmissionStatus }): JSX.Element {
  const className = 'h-4 w-4 shrink-0';
  return status === 'EVALUATED' ? (
    <CheckCircle2 className={`${className} text-primary`} aria-hidden="true" />
  ) : (
    <Clock className={`${className} text-muted-foreground`} aria-hidden="true" />
  );
}

/**
 * "Setoran" history (Phase G-2, Job 10) - every submission the student has
 * ever recorded, across all their targets. `teacherId` is a raw user id
 * with no name-resolution endpoint anywhere in this app (the same
 * limitation G-1's student creation form already documented), so it is
 * never rendered as if it were a person's name - only whether a Teacher
 * recorded it is shown.
 */
export function SubmissionList({ studentId }: { readonly studentId: string }): JSX.Element {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useSubmissions(studentId, page, PAGE_SIZE);
  const canCreate = useHasCapability('memorization_records:manage');
  const surahs = useSurahLookup();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Setoran</CardTitle>
        {canCreate && (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Catat Setoran
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat setoran.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada setoran.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
          <>
            <ul className="flex flex-col gap-2">
              {data.items.map((submission) => (
                <li key={submission.id}>
                  <Link to={`/students/${studentId}/setoran/${submission.id}`}>
                    <div
                      className={`flex flex-col gap-2 rounded-md border p-3 transition-colors hover:bg-secondary/50 sm:flex-row sm:items-center sm:justify-between ${
                        submission.status === 'EVALUATED'
                          ? 'border-primary/30 bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <StatusIcon status={submission.status} />
                        <div className="flex flex-col gap-1">
                          <span className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-sm font-medium">
                              {surahs.get(submission.surah) !== undefined
                                ? `${String(submission.surah)}. ${surahs.get(submission.surah)?.nameLatin ?? ''}`
                                : `Surah ${String(submission.surah)}`}
                              : Ayat {submission.startAyah}
                              {submission.endAyah !== submission.startAyah
                                ? `-${submission.endAyah}`
                                : ''}
                            </span>
                            {surahs.get(submission.surah) !== undefined && (
                              <span lang="ar" dir="rtl" className="text-sm text-muted-foreground">
                                {surahs.get(submission.surah)?.nameArabic}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(submission.submittedAt).toLocaleString('id-ID')}
                            {submission.teacherId !== null ? ' · Dicatat oleh guru' : ''}
                          </span>
                        </div>
                      </div>
                      <Badge variant={submission.status === 'EVALUATED' ? 'default' : 'secondary'}>
                        {SUBMISSION_STATUS_LABELS[submission.status]}
                      </Badge>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {data.total_pages > 1 && (
              <div className="flex items-center justify-between gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => Math.max(1, current - 1));
                  }}
                >
                  Sebelumnya
                </Button>
                <span className="text-xs text-muted-foreground">
                  Halaman {data.page} dari {data.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= data.total_pages}
                  onClick={() => {
                    setPage((current) => Math.min(data.total_pages, current + 1));
                  }}
                >
                  Berikutnya
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <CreateSubmissionDialog
        studentId={studentId}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </Card>
  );
}
